import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { CalendarClock, Flag } from 'lucide-react';
import { ApiError, apiFetch } from '../api/client.ts';
import { Card } from '../components/Card.tsx';
import { PortalFooter } from '../components/PortalFooter.tsx';
import { PortalHealthBanner } from '../components/PortalHealthBanner.tsx';
import { PortalNavBar } from '../components/PortalNavBar.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';

interface PortalContact {
  id: string;
  email: string;
  name: string | null;
  isRequester: boolean;
  isOrgAdmin: boolean;
  isOrgBilling: boolean;
}

interface PortalProject {
  id: string;
  name: string;
  status: 'active' | 'completed' | 'cancelled';
  billingMode: 'flat_fee' | 'block_hours';
  budgetedHours: number;
  consumedMinutes: number;
  startedAt: string;
  completedAt: string | null;
  dueDate: string | null;
  cutoverDate: string | null;
  subtaskTotal: number;
  subtaskCompleted: number;
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  // YYYY-MM-DD strings render in any locale; for ISO datetimes, slice off
  // the time before parsing so a UTC midnight doesn't display as the prior
  // day in negative-offset zones.
  const d = new Date(iso.length === 10 ? iso + 'T00:00:00' : iso);
  return d.toLocaleDateString();
}

function statusPill(status: PortalProject['status']): React.ReactElement {
  const cls =
    status === 'active'
      ? 'bg-emerald-50 text-emerald-700'
      : status === 'completed'
        ? 'bg-slate-100 text-slate-700'
        : 'bg-[var(--color-surface)] text-[var(--color-muted)]';
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${cls}`}>
      {status}
    </span>
  );
}

export function PortalProjectsListPage(): React.ReactElement {
  useDocumentTitle('Projects');
  const navigate = useNavigate();
  const [contact, setContact] = useState<PortalContact | null>(null);
  const [projects, setProjects] = useState<PortalProject[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiFetch<{ contact: PortalContact | null }>('/api/portal/auth/me')
      .then((r) => {
        if (cancelled) return;
        if (!r.contact) {
          navigate('/login', { replace: true, state: { from: '/portal/projects' } });
          return;
        }
        setContact(r.contact);
      })
      .catch(() => {
        if (!cancelled) navigate('/login', { replace: true });
      });
    apiFetch<{ projects: PortalProject[] }>('/api/portal/projects')
      .then((r) => {
        if (!cancelled) setProjects(r.projects);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof ApiError ? e.message : 'Failed to load projects.');
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  return (
    <div className="min-h-screen bg-[var(--color-surface)]">
      <PortalNavBar contact={contact} />
      <PortalHealthBanner />
      <main className="mx-auto max-w-5xl space-y-6 px-6 py-8">
        <div>
          <h1 className="text-3xl font-bold text-[var(--color-header)]">Projects</h1>
          <p className="mt-1 text-[var(--color-muted)]">
            Active and past projects for your organization. Click any project to see its full
            timeline, milestones, and tickets.
          </p>
        </div>

        {error && <p className="text-sm text-[var(--color-danger)]">{error}</p>}

        {projects === null ? (
          <p className="text-[var(--color-muted)]">Loading…</p>
        ) : projects.length === 0 ? (
          <Card>
            <p className="text-sm text-[var(--color-muted)]">
              No projects yet. When T3 spins up a project for your organization, it'll appear here.
            </p>
          </Card>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {projects.map((p) => {
              const consumedHours = p.consumedMinutes / 60;
              const pct = p.budgetedHours > 0 ? Math.min(100, (consumedHours / p.budgetedHours) * 100) : 0;
              return (
                <Link key={p.id} to={`/portal/projects/${p.id}`} className="block">
                  <Card className="h-full transition hover:border-[var(--color-t3-blue)]">
                    <div className="flex items-start justify-between gap-3">
                      <h2 className="text-base font-semibold text-[var(--color-header)]">
                        {p.name}
                      </h2>
                      {statusPill(p.status)}
                    </div>
                    <dl className="mt-3 grid grid-cols-2 gap-3 text-xs">
                      <div>
                        <dt className="uppercase tracking-wide text-[var(--color-muted)]">Due</dt>
                        <dd className="mt-0.5 inline-flex items-center gap-1 text-sm">
                          <CalendarClock className="h-3.5 w-3.5 text-[var(--color-muted)]" />
                          {fmtDate(p.dueDate)}
                        </dd>
                      </div>
                      <div>
                        <dt className="uppercase tracking-wide text-[var(--color-muted)]">Cutover</dt>
                        <dd className="mt-0.5 inline-flex items-center gap-1 text-sm">
                          <Flag className="h-3.5 w-3.5 text-[var(--color-muted)]" />
                          {fmtDate(p.cutoverDate)}
                        </dd>
                      </div>
                      <div>
                        <dt className="uppercase tracking-wide text-[var(--color-muted)]">Hours</dt>
                        <dd className="mt-0.5 text-sm">
                          {consumedHours.toFixed(1)}h / {p.budgetedHours}h
                        </dd>
                      </div>
                      <div>
                        <dt className="uppercase tracking-wide text-[var(--color-muted)]">Subtasks</dt>
                        <dd className="mt-0.5 text-sm">
                          {p.subtaskCompleted} / {p.subtaskTotal}
                        </dd>
                      </div>
                    </dl>
                    {p.budgetedHours > 0 && (
                      <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-[var(--color-surface)]">
                        <div
                          className="h-full bg-[var(--color-t3-blue)] transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    )}
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </main>
      <PortalFooter />
    </div>
  );
}
